/**
 * DateRangeInput wraps DateRangePicker from React-dates and gives a list of all default props we use.
 * Styles for DateRangePicker can be found from 'public/reactDates.css'.
 *
 * N.B. *isOutsideRange* in defaultProps is defining what dates are available to booking.
 */
import React, { Component } from 'react';
import { bool, func, instanceOf, node, oneOf, shape, string } from 'prop-types';
import { DateRangePicker, DayPickerRangeController } from 'react-dates';
import classNames from 'classnames';
import moment from 'moment';

import { intlShape, injectIntl } from '../../util/reactIntl';
import { FormattedMessage } from '../../util/reactIntl';
import { START_DATE, END_DATE } from '../../util/dates';

import { Button, IconArrowHead } from '../../components';
import css from './DateRangeInput.module.css';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

// When the unit type is day, the endDate of booking range is exclusive.
// In the UI picker, we show only inclusive dates
const apiEndDateToPickerDate = (isDaily, endDate) => {
  const isValid = endDate instanceof Date;

  // API end dates are exlusive, so we need to shift them with daily
  // booking.
  return isValid && isDaily
    ? moment(endDate).subtract(1, 'days')
    : isValid
      ? moment(endDate)
      : null;
};

// When the unit type is day, the endDate of booking range is exclusive.
// In the UI picker, we show only inclusive dates
const pickerEndDateToApiDate = (isDaily, endDate) => {
  const isValid = endDate instanceof moment;

  // API end dates are exlusive, so we need to shift them with daily
  // booking.
  return isValid && isDaily
    ? endDate
      .clone()
      .add(1, 'days')
      .toDate()
    : isValid
      ? endDate.toDate()
      : null;
};

// Since final-form tracks the onBlur event for marking the field as
// touched (which triggers possible error validation rendering), only
// trigger the event asynchronously when no other input within this
// component has received focus.
//
// This prevents showing the validation error when the user selects a
// value and moves on to another input within this component.
const BLUR_TIMEOUT = 100;

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

// Possible configuration options of React-dates
const defaultProps = {
  initialDates: null, // Possible initial date passed for the component
  value: null, // Value should keep track of selected date.

  // input related props
  startDateId: 'startDate',
  endDateId: 'endDate',
  startDatePlaceholderText: null, // Handled inside component
  endDatePlaceholderText: null, // Handled inside component
  disabled: false,
  required: false,
  readOnly: false,
  screenReaderInputMessage: null, // Handled inside component
  showClearDates: false,
  showDefaultInputIcon: false,
  customArrowIcon: <span />,
  customInputIcon: null,
  customCloseIcon: null,
  noBorder: true,
  block: false,

  // calendar presentation and interaction related props
  renderMonthText: null,
  orientation: HORIZONTAL_ORIENTATION,
  anchorDirection: ANCHOR_LEFT,
  horizontalMargin: 0,
  withPortal: false,
  withFullScreenPortal: false,
  appendToBody: false,
  disableScroll: false,
  daySize: 38,
  isRTL: false,
  initialVisibleMonth: null,
  // This gets default value at FieldDateRangeInput
  firstDayOfWeek: 0,
  numberOfMonths: 1,
  keepOpenOnDateSelect: false,
  reopenPickerOnClearDates: false,
  renderCalendarInfo: null,
  hideKeyboardShortcutsPanel: true,

  // navigation related props
  navPrev: <PrevIcon />,
  navNext: <NextIcon />,
  onPrevMonthClick() { },
  onNextMonthClick() { },
  transitionDuration: 200, // milliseconds between next month changes etc.

  renderCalendarDay: undefined, // If undefined, renders react-dates/lib/components/CalendarDay
  // day presentation and interaction related props
  renderDayContents: day => {
    return <span className="renderedDay">{day.format('D')}</span>;
  },
  minimumNights: 0,
  enableOutsideDays: false,
  isDayBlocked: () => () => false,

  // This gets default value at FieldDateRangeInput
  isOutsideRange: day => false,
  isDayHighlighted: () => { },

  // Internationalization props
  // Multilocale support can be achieved with displayFormat like moment.localeData.longDateFormat('L')
  // https://momentjs.com/
  displayFormat: 'ddd, MMM D',
  monthFormat: 'MMMM YYYY',
  weekDayFormat: 'dd',
};

class DateRangeInputComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      focusedInput: this.props.focusedInput,
      currentStartDate: null,
      startInputValue: '',
      endInputValue: '',
      isMobile: typeof window !== 'undefined' ? window.innerWidth <= 1024 : false, // Add mobile state
      showInitialHighlight: true, // Track if highlight should be shown
      showEndHighlight: false, // Track if end date should be highlighted
    };
    this.blurTimeoutId = null;
    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate(prevProps) {
    // Update focusedInput in case a new value for it is
    // passed in the props. This may occur if the focus
    // is manually set to the date picker.
    //
    // Regression fix (manual QA, cart/storefront/rental-default prefill landing on an
    // empty, reopened calendar): this used to only sync when the INCOMING prop was
    // truthy (`this.props.focusedInput && ...`), so a parent clearing focus back to
    // undefined/null - which BookingDatesForm.js does the instant an async prefill
    // resolves, specifically to stop the calendar looking focused - was silently
    // ignored here. `this.state.focusedInput` (not the prop) is what actually gets
    // handed to react-dates' DayPickerRangeController/DateRangePicker below, so it
    // stayed stuck on whatever truthy value it last saw (typically START_DATE, seeded
    // at this component's own mount) no matter what the parent did afterward. With
    // focusedInput genuinely still START_DATE from react-dates' own point of view, it
    // reacts completely normally when startDate/endDate then arrive with real values -
    // by calling onFocusChange(START_DATE) itself, which round-trips back up through
    // FieldDateRangeInput.js's 300ms-debounced onFocus and lands on BookingDatesForm's
    // own onFocus handler, which treats any focus of START_DATE as "user wants to
    // re-pick dates" and wipes the booking dates it was just given - a few hundred
    // milliseconds after they were correctly applied, with no user interaction at all.
    // Mirroring the prop through unconditionally - clears included - keeps this
    // component's local state in actual sync with the parent's controlled value.
    if (this.props.focusedInput !== prevProps.focusedInput) {
      this.setState({ focusedInput: this.props.focusedInput });
    }
    // Sync input values with props when calendar selection changes
    if (
      this.props.value &&
      (this.props.value.startDate !== (prevProps.value && prevProps.value.startDate) ||
        this.props.value.endDate !== (prevProps.value && prevProps.value.endDate))
    ) {
      this.setState({
        startInputValue: this.props.value.startDate
          ? moment(this.props.value.startDate).format('YYYY-MM-DD')
          : '',
        endInputValue: this.props.value.endDate
          ? moment(this.props.value.endDate).format('YYYY-MM-DD')
          : '',
        showInitialHighlight: !this.props.value.startDate, // Show initial highlight if no start date
        showEndHighlight: this.props.value.startDate && !this.props.value.endDate, // Show end highlight if start date but no end date
      });
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
      window.clearTimeout(this.blurTimeoutId);
    }
  }

  handleResize = () => {
    if (typeof window !== 'undefined') {
      this.setState({ isMobile: window.innerWidth <= 1024 });
    }
  };

  onDatesChange(dates) {
    const { isDaily, isBlockedBetween } = this.props;
    const { startDate, endDate } = dates;

    // both dates are selected, a new start date before the previous start
    // date is selected
    const startDateUpdated =
      isBlockedBetween &&
      startDate &&
      endDate &&
      this.state.currentStartDate &&
      startDate.isBefore(this.state.currentStartDate);

    let startDateAsDate = startDate instanceof moment ? startDate.startOf('day').toDate() : null;
    let endDateAsDate = pickerEndDateToApiDate(isDaily, endDate);

    if (startDateUpdated) {
      // clear the end date in case a blocked date can be found
      // between previous start date and new start date
      const clearEndDate = isBlockedBetween(
        startDate,
        moment(this.state.currentStartDate).add(1, 'days')
      );
      endDateAsDate = clearEndDate ? null : pickerEndDateToApiDate(isDaily, endDate);
    }

    this.setState(() => ({
      currentStartDate: startDateAsDate,
      showInitialHighlight: startDate ? false : this.state.showInitialHighlight, // Remove highlight if startDate selected
      showEndHighlight: startDate && !endDate ? true : endDate ? false : this.state.showEndHighlight, // Show end highlight if startDate selected but no endDate, remove if endDate selected
    }));

    this.props.onChange({ startDate: startDateAsDate, endDate: endDateAsDate });
  }

  onFocusChange(focusedInput) {
    // DateRangePicker requires 'onFocusChange' function and 'focusedInput'
    // but Fields of React-Form deals with onFocus & onBlur instead
    this.setState({
      focusedInput,
      showInitialHighlight: false, // Remove highlight on any focus
      showEndHighlight: false, // Remove end highlight on any focus
    });

    if (focusedInput) {
      window.clearTimeout(this.blurTimeoutId);
      this.props.onFocus(focusedInput);
    } else {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = window.setTimeout(() => {
        this.props.onBlur();
      }, BLUR_TIMEOUT);

      // If no dates are selected, keep the start-date error highlight active.
      const hasStart = !!(this.props.value && this.props.value.startDate);
      const hasEnd = !!(this.props.value && this.props.value.endDate);
      this.setState({
        showInitialHighlight: !hasStart,
        showEndHighlight: hasStart && !hasEnd,
      });

      if (this.props.value?.startDate && this.props.value?.endDate) {
        this.props.onApply();
      };
    }
  }

  handleCancel = () => {
    // Reset the selection (clear dates)
    this.props.onChange({ startDate: null, endDate: null });
    // Reset local focus to START_DATE (not null) so the calendar stays active and
    // ready for a new selection. We can't rely solely on props.onRemove() to push
    // this down: the parent's focusedInput state is already START_DATE in the
    // no-prior-interaction case, so its setFocusedInput(START_DATE) call is a
    // same-value no-op that React bails out on, and this local state would
    // otherwise be stuck at null with no day-click ever able to reset it.
    this.setState({
      focusedInput: START_DATE,
      showInitialHighlight: true, // Reset to initial state
      showEndHighlight: false, // Reset end highlight
    });
    this.props.onRemove();
  };

  handleApply = () => {
    // Just close the picker, selection is already set
    this.setState({
      focusedInput: null,
      showInitialHighlight: false, // Remove highlights when applied
      showEndHighlight: false, // Remove end highlight when applied
    });
    // Optionally, notify parent
    if (this.props.onApply) {
      this.props.onApply();
    }
  };

  handleStartInputChange = (e) => {
    this.setState({ startInputValue: e.target.value });
  };

  handleEndInputChange = (e) => {
    this.setState({ endInputValue: e.target.value });
  };

  handleStartInputBlur = () => {
    const { startInputValue } = this.state;
    const { value } = this.props;
    const date = moment(startInputValue, 'YYYY-MM-DD', true);
    if (date.isValid()) {
      this.onDatesChange({
        startDate: date,
        endDate: value && value.endDate ? moment(value.endDate) : null,
      });
    }
  };

  handleEndInputBlur = () => {
    const { endInputValue } = this.state;
    const { value } = this.props;
    const date = moment(endInputValue, 'YYYY-MM-DD', true);
    if (date.isValid()) {
      this.onDatesChange({
        startDate: value && value.startDate ? moment(value.startDate) : null,
        endDate: date,
      });
    }
  };


  render() {
    const {
      className,
      isDaily,
      initialDates,
      intl,
      name,
      startDatePlaceholderText,
      endDatePlaceholderText,
      onBlur,
      onChange,
      onFocus,
      screenReaderInputMessage,
      useMobileMargins,
      value,
      children,
      render,
      isBlockedBetween,
      isDayBlocked,
      isOutsideRange,
      id,
      isBlocked,
      savingPerDayMessage,
      onApply,
      onRemove,
      applyInProgress,
      // Calendar-only mode (no visible Start/End Date input fields, single month,
      // always rendered) - used pre-Apply to match Figma's "First State". Once
      // applied, falls back to the normal DateRangePicker (with its own collapsed
      // input-boxes view) for showing the confirmed dates.
      inline,
      clearButtonLabel,
      navPrev,
      navNext,
      onPrevMonthClick,
      onNextMonthClick,
      renderCalendarDay,
      renderDayContents,
      firstDayOfWeek,
      hideKeyboardShortcutsPanel,
      initialVisibleMonth,
      monthFormat,
      weekDayFormat,
      isRTL,
      transitionDuration,
      noBorder,
      orientation,
      isDayHighlighted,
      // RAS-2525 (mobile booking modal, manual QA fix): additional class applied to
      // the Clear/Apply footer only - undefined for every other consumer of this
      // shared component (CartDrawer, search page, StoreProfilePage), so their own
      // rendering is completely unaffected.
      footerClassName,
      ...datePickerProps
    } = this.props;

    const initialStartMoment = initialDates ? moment(initialDates.startDate) : null;
    const initialEndMoment = initialDates ? moment(initialDates.endDate) : null;
    const startDate =
      value && value.startDate instanceof Date ? moment(value.startDate) : initialStartMoment;
    const endDate =
      apiEndDateToPickerDate(isDaily, value ? value.endDate : null) || initialEndMoment;

    const startDatePlaceholderTxt =
      startDatePlaceholderText ||
      intl.formatMessage({ id: 'FieldDateRangeInput.startDatePlaceholderText' });
    const endDatePlaceholderTxt =
      endDatePlaceholderText ||
      intl.formatMessage({ id: 'FieldDateRangeInput.endDatePlaceholderText' });
    const screenReaderInputText =
      screenReaderInputMessage ||
      intl.formatMessage({ id: 'FieldDateRangeInput.screenReaderInputMessage' });

    const classes = classNames(css.inputRoot, className, {
      [css.withMobileMargins]: useMobileMargins,
      [css.initialStartHighlight]: this.state.showInitialHighlight, // Add this line
      [css.endDateHighlight]: this.state.showEndHighlight, // Add end date highlight
    });
    const startInputValue = this.state.startInputValue;
    const endInputValue = this.state.endInputValue;

    const renderCalendarInfo = () => (
      <div className={classNames(css.customFooter, footerClassName)}>
        <div className={css.customFooterContent}>
          <div className={css.savingPerDayMessage}>
            {savingPerDayMessage || (
              <FormattedMessage
                id="RentalRatesSheet.tagline"
                defaultMessage="The longer you rent, the more you save."
              />
            )}
          </div>
          <div className={css.buttonWrapper}>
            <Button
              type="button"
              className={css.cancelButton}
              onClick={this.handleCancel}
              disabled={applyInProgress}
            >
              {clearButtonLabel || (
                <FormattedMessage id="FieldDateRangeInput.cancel" defaultMessage="Cancel" />
              )}
            </Button>
            <Button
              type="button"
              className={css.applyButton}
              onClick={this.handleApply}
              disabled={!startDate || !endDate || applyInProgress}
              inProgress={applyInProgress}
            >
              <FormattedMessage id="FieldDateRangeInput.apply" defaultMessage="Apply" />
            </Button>
          </div>
        </div>
      </div>
    );

    // Props shared between the calendar-only (inline) and input+popup renderers -
    // everything DayPickerRangeController and DateRangePicker both accept.
    const sharedCalendarProps = {
      isDayBlocked: isDayBlocked(this.state.focusedInput),
      isOutsideRange: isOutsideRange(this.state.focusedInput),
      isDayHighlighted,
      focusedInput: this.state.focusedInput,
      onFocusChange: this.onFocusChange,
      startDate,
      endDate,
      minimumNights: isDaily ? 0 : 1,
      onDatesChange: this.onDatesChange,
      // react-dates computes the CalendarMonth/CalendarMonthGrid container width
      // directly from this number (7 * daySize + 2 * horizontalMonthPadding + 1),
      // independent of any CSS - it must match the 40px cell size the CSS enforces
      // (.CalendarDay, Figma spec) or the container ends up wider than its actual
      // day content, showing as a stray edge/line.
      daySize: 40,
      // Figma's "First State" spec pads the calendar content 24px on each side
      // (spacing-3xl token). react-dates defaults horizontalMonthPadding to 13px,
      // which is baked into the same width formula as daySize above - left at the
      // default, the whole calendar box renders ~22px narrower than the mock
      // regardless of daySize being correct. The DayPickerNavigation_button
      // left:24px/right:24px offsets in DateRangeInput.module.css already assume
      // this 24px padding, so this brings the actual content width into agreement
      // with where those nav arrows are positioned.
      horizontalMonthPadding: 24,
      keepOpenOnDateSelect: true,
      navPrev,
      navNext,
      onPrevMonthClick,
      onNextMonthClick,
      renderCalendarDay,
      renderDayContents,
      firstDayOfWeek,
      hideKeyboardShortcutsPanel,
      initialVisibleMonth,
      monthFormat,
      weekDayFormat,
      isRTL,
      transitionDuration,
      noBorder,
      orientation,
      renderCalendarInfo,
    };

    return (
      <div className={classes}>
        {inline ? (
          <DayPickerRangeController
            {...sharedCalendarProps}
            numberOfMonths={1}
            enableOutsideDays={this.state.isMobile}
          />
        ) : (
          <DateRangePicker
            {...datePickerProps}
            {...sharedCalendarProps}
            startDatePlaceholderText={startDatePlaceholderTxt}
            endDatePlaceholderText={endDatePlaceholderTxt}
            screenReaderInputMessage={screenReaderInputText}
            numberOfMonths={this.state.isMobile ? 1 : 2}
            enableOutsideDays={this.state.isMobile}
          />
        )}
      </div>
    );
  }
}

DateRangeInputComponent.defaultProps = {
  className: null,
  useMobileMargins: false,
  inline: false,
  clearButtonLabel: null,
  applyInProgress: false,
  footerClassName: null,
  ...defaultProps,
};

DateRangeInputComponent.propTypes = {
  className: string,
  inline: bool,
  clearButtonLabel: node,
  applyInProgress: bool,
  footerClassName: string,
  startDateId: string,
  endDateId: string,
  isDaily: bool.isRequired,
  focusedInput: oneOf([START_DATE, END_DATE]),
  initialDates: instanceOf(Date),
  name: string.isRequired,
  isBlockedBetween: func,
  isDayBlocked: func,
  isOutsideRange: func,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  onFocus: func.isRequired,
  useMobileMargins: bool,
  startDatePlaceholderText: string,
  endDatePlaceholderText: string,
  screenReaderInputMessage: string,
  value: shape({
    startDate: instanceOf(Date),
    endDate: instanceOf(Date),
  }),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(DateRangeInputComponent);